class ReleaseTimer extends HTMLElement {
  constructor() {
    super();

    this.releaseTimestamp = Number(this.dataset.releaseTimestamp);
    this.timerContainer = this.querySelector('.release-hero__countdown-timer');

    if (this.timerContainer) {
      this.daysEl = this.timerContainer.querySelector('[data-days]');
      this.hoursEl = this.timerContainer.querySelector('[data-hours]');
      this.minsEl = this.timerContainer.querySelector('[data-minutes]');
      this.secsEl = this.timerContainer.querySelector('[data-seconds]');
    }

    // Tracks whether we've seen a positive distance during this page's lifetime.
    // We only call refresh() when the countdown actually transitions from
    // positive -> non-positive; otherwise a page loaded after the release date
    // would refetch immediately, get the same HTML back, and loop forever.
    this.hasTickedDown = false;
  }

  connectedCallback() {
    // No timestamp (article missing release date) or no countdown markup
    // (already-released state rendered by Liquid) — nothing to count down.
    if (!this.releaseTimestamp || !this.timerContainer) return;

    this.tick();
    this.intervalId = setInterval(this.tick.bind(this), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  tick() {
    // Liquid's `date: '%s'` produces a Unix timestamp in the shop's timezone
    // ("America/Vancouver"), which is an absolute moment in time. Comparing it
    // to the user's `Date.now()` gives the correct remaining time regardless
    // of the user's local timezone — no offset math needed.
    const nowSeconds = Math.floor(Date.now() / 1000);
    const distance = this.releaseTimestamp - nowSeconds;

    if (distance <= 0) {
      this.disconnectedCallback();
      if (this.hasTickedDown) {
        this.refresh();
      }
      return;
    }

    this.hasTickedDown = true;

    const days = Math.floor(distance / 86400);
    const hours = Math.floor((distance % 86400) / 3600);
    const minutes = Math.floor((distance % 3600) / 60);
    const seconds = distance % 60;

    if (this.daysEl) this.daysEl.textContent = this.pad(days);
    if (this.hoursEl) this.hoursEl.textContent = this.pad(hours);
    if (this.minsEl) this.minsEl.textContent = this.pad(minutes);
    if (this.secsEl) this.secsEl.textContent = this.pad(seconds);
  }

  pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  async refresh() {
    // Once we cross the release timestamp, re-fetch the current page so Liquid
    // can re-render the section in its "released" state, then swap just this
    // custom element with the freshly rendered one.
    try {
      const response = await fetch(window.location.href, { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to refetch page');

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      // Match by the parent section wrapper id so we update the right instance
      // when multiple release heroes share the page.
      const wrapper = this.closest('[id^="ReleaseHero-"]');
      const newTimer = wrapper
        ? doc.getElementById(wrapper.id)?.querySelector('release-timer')
        : doc.querySelector('release-timer');

      if (newTimer) {
        this.replaceWith(newTimer);
      } else {
        window.location.reload();
      }
    } catch (e) {
      window.location.reload();
    }
  }

}

customElements.define('release-timer', ReleaseTimer);
